'use strict';

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import Select from 'react-select';
import cn from 'classnames/bind';

import { appSelectors, appOperations } from '@/state/domains/app';
import { usersOperations, usersSelectors } from '@/state/domains/users';
import { ordersOperations, ordersSelectors } from '@/state/domains/orders';
import { attemptsOperations, attemptsSelectors } from '@/state/domains/attempts';
import { assignmentsOperations, assignmentsSelectors } from '@/state/domains/assignments';
import { locationsSelectors } from '@/state/domains/locations';

import Loading from '@/views/common/Loading';
import FormsPage from './FormsPage'; 
import ServePage from './ServePage'; 
import PostPage from './PostPage';
import BadAddressPage from './BadAddressPage';
import PickupPage from './PickupPage';
import DeliverPage from './DeliverPage';

import Link from '@/views/common/Link';
import Attempts from './Attempts'; 
import AttemptForm from './AttemptForm'; 
import ServeForm from './ServeForm'; 
import DeliverForm from './DeliverForm'; 
import BadAddressForm from './BadAddressForm';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import s from './styles.scss';

const sx = cn.bind(s);

/*
			residential_properties: {
				Single-Family Home
				Duplex/Triplex
				Condominium
				Townhouse
				High-rise Apartment
				Mid-rise Apartment
				Garden-style Apartment
				Walk-up
				Mother-in-law Apartment
				Manufactured Housing Community

			}
		*/

const ToLink = ({ id, direction }) => {

	const icon = <FontAwesomeIcon
		icon={ `angle-${ direction }` }
		size='3x'
		color='#000'
	/>;

	if (id) {
		return <Link to={ id }>{ icon }</Link>;
	}

	return <span className={ s.trans }>{ icon }</span>;
};

export const RecipientSection = connect(
	(state, props) => ({
		actionMode: appSelectors.getActionMode(state, props),
		location: locationsSelectors.getPageLocation(state, props),
		nextUrl: assignmentsSelectors.getNextUrl(state, props),
		prevUrl: assignmentsSelectors.getPrevUrl(state, props),
	}),
	{
	}
)(
	({ job, location, actionMode, prevId, nextUrl, prevUrl }) => {

		if (!job) {
			return <div />;
		}

		const recipient = job.locations.find(l => l.locationId === location._id).recipient;

		const { streetNumber, streetName, subunit, city, state, zip } = location;

		const addressNorm = (`${ streetNumber || '' } ${ streetName || '' }`).trim();

		return (
			<div className={ s.recipientCont } >
				<div className={ s.recipientName } >{ recipient }</div>
				<div className={ s.recipient }>
					{ actionMode === 'select' &&
						<div className={ s.locBtn }>
							<ToLink id={ prevUrl } direction='left' />
						</div>
					}
					<div className={ s.addressColumn }>
						<div className={ s.addressRows }>
							{ addressNorm && <div className={ s.address }>{ addressNorm }</div> }
							{ subunit && <div>{ subunit }</div> }
							<div>{ !!city && `${ city }, ${ state } ${ zip }` }</div>
						</div>
					</div>
					{ actionMode === 'select' &&
						<div className={ s.locBtn }>
							<ToLink id={ nextUrl } direction='right' />
						</div>
					}
				</div>
			</div>
		);
	}
);

const ActionBtns = ({
	username,
	date,
	job,
	location,
	setActionMode,
	task,
	postCompleteTask,
}) => {

	let completeMode = 'serve';
	let completeIcon = 'scale-balanced';
	let completeText = 'Serve';

	if (task === 'DELIVERY') {
		completeMode = 'deliver';
		completeIcon = 'truck';
		completeText = 'Deliver';

	} else if (task === 'PICKUP') {
		completeMode = 'pickup';
		completeIcon = 'hand-holding';
		completeText = 'Pickup';
	}

	const completeTask = (type, geo = []) => postCompleteTask(
		username,
		date,
		job._id,
		location._id,
		type,
		{ geo }
	);

	const geolocation = window.navigator.geolocation;

	const pickup = () => {

		geolocation.getCurrentPosition(
			pos => {
				const { coords } = pos;
				const { latitude, longitude } = coords;
				const geo = [ latitude, longitude ];

				completeTask('PICKUP', geo);
			},
			err => {
				completeTask('PICKUP');
			},
			{
				timeout: 5000,
				maximumAge: 0,
				enableHighAccuracy: true
			}
		);
	};

	const post = () => {

		geolocation.getCurrentPosition(
			pos => {
				const { coords } = pos;
				const { latitude, longitude } = coords;
				const geo = [ latitude, longitude ];

				completeTask('POST', geo);
			},
			err => {
				completeTask('POST');
			},
			{
				timeout: 5000,
				maximumAge: 0,
				enableHighAccuracy: true
			}
		);
	};

	const completeRow = completeMode === 'serve' ?
		(
			<div className={ s.double }>
				<div
					className={ sx('dbutton', 'serve') }
					onClick={ () => setActionMode(completeMode) }
				>
					<span className={ s.icon }>
						<FontAwesomeIcon icon={ completeIcon } color='#fff'/>
					</span>
					Serve
				</div>
				<div
					className={ sx('dbutton', 'post') }
					onClick={ post }
				>
					Post
				</div>
			</div>
		) :
		(
			<button
				onClick={
					() => task === 'PICKUP' ?
						pickup() :
						setActionMode(completeMode)
				}
				className={ s.serve }
			>
				<span className={ s.icon }>
					<FontAwesomeIcon icon={ completeIcon } color='#fff'/>
				</span>
				<span className={ s.txt }>{ completeText }</span>
			</button>
		);

	return (
		<div className={ s.actionTabs }>
			<button
				onClick={ () => setActionMode('attempt') }
				className={ s.attempt }
			>
				<span className={ s.icon }>
					<FontAwesomeIcon icon='door-closed' color='#ff'/>
				</span>
				<span className={ s.txt }>Attempt</span>
			</button>
			{ completeRow }
			<button
				onClick={ () => setActionMode('badAddress') }
				className={ s.badAddress }
			>
				<span className={ s.icon }>
					<FontAwesomeIcon icon='ban' color='#fff'/>
				</span>
				<span className={ s.txt }>Bad Address</span>
			</button>
		</div>
	);
};

const SelectForm = connect(
	(state, props) => ({
		attempt: attemptsSelectors.getPropAttempt(state, props),
	}),
	{
	}
)(
	({
		username,
		date,
		job,
		location,
		jobLocation: jobL,
		attempt,
		setActionMode,
		prevId,
		nextId,
		canEdit,
		postCompleteTask,
	}) => {

		let resultsPage;

		if (!job || !location) {
			return <div />;
		}

		if (jobL.completed) {

			if (jobL.invalid) {
				resultsPage = <BadAddressPage 
					job={ job }
					location={ location }
					canEdit={ canEdit }
				/>;
			} else {

				switch (jobL.task) {

					case 'SERVICE':

						if (attempt && attempt.type === 'POST') {
							resultsPage = <PostPage
								job={ job }
								location={ location }
								canEdit={ canEdit }
							/>;
						} else {
							resultsPage = <ServePage
								job={ job }
								location={ location }
								canEdit={ canEdit }
							/>;
						}
						break;

					case 'PICKUP':
						resultsPage = <PickupPage
							job={ job }
							location={ location }
							canEdit={ canEdit }
						/>;
						break;

					case 'DELIVERY':
						resultsPage = <DeliverPage
							job={ job }
							location={ location }
							canEdit={ canEdit }
						/>;
						break;
				}
			}
		} else {

			resultsPage = (
				<>
					{
						canEdit &&
						<ActionBtns
							username={ username }
							date={ date }
							job={ job }
							location={ location }
							task={ jobL.task }
							setActionMode={ setActionMode }
							postCompleteTask={ postCompleteTask }
						/>
					}
					<Attempts job={ job } location={ location } />
				</>
			);
		}

		return (
			<div className={ s.column }>
				<RecipientSection
					actionMode='select'
					job={ job }
				/>
				{ resultsPage }
			</div>
		);
	}
);

const Main = connect(
	(state, props) => ({
		pageUrl: appSelectors.getPageUrl(state, props),
		isPageLoaded: appSelectors.isPageLoaded(state, props),
		urlParams: appSelectors.getUrlParams(state, props),
		date: appSelectors.getPageDate(state, props),
		jobId: appSelectors.getPageJobId(state, props),
		actionMode: appSelectors.getActionMode(state, props),
		username: appSelectors.getPageUsername(state, props),
		pageUser: usersSelectors.getPageUser(state, props),
		job: ordersSelectors.getPageJob(state, props),
		location: locationsSelectors.getPageLocation(state, props),
		jobLocation: ordersSelectors.getPageJobLocation(state, props),
		canEdit: usersSelectors.userCan(state, { ...props, action: 'userRoute:edit' }),
		getNext: assignmentsSelectors.getNext(state, props),
	}),
	{
		setPageLoaded: appOperations.setPageLoaded,
		setPageContext: appOperations.setPageContext,
		setActionMode: appOperations.setActionMode,
		fetchUser: usersOperations.fetchUser,
		fetchAttempts: attemptsOperations.fetchAttempts,
		getAssignments: assignmentsOperations.getAssignments,
		postCompleteTask: ordersOperations.postCompleteTask,
	}
)(
	({
		pageUrl,
		isPageLoaded,
		urlParams,
		username,
		date,
		pageUser,
		job,
		jobId,
		location,
		jobLocation,
		actionMode,
		canEdit,
		setPageLoaded,
		setActionMode,
		setPageContext,
		getAssignments,
		fetchAttempts,
		postCompleteTask,
		getNext,
	}) => {
		useEffect(
			() => {
				if (!isPageLoaded) {

					if (!pageUser) {
						fetchUser(username);
					} else {
						const { jobId, companyId, hashLocationId } = urlParams;

						const p1 = getAssignments(username, [ date ]);

						const p2 = fetchAttempts(
							companyId,
							jobId,
							hashLocationId
						);

						Promise.all([ p1, p2 ]).then(
							([ r1, r2 ]) => {
								if (r1.success && r2.success) {
									setPageLoaded(pageUrl);
								}
							}
						);
					}
				}
			},
			[ pageUrl, pageUser ]
		);

		if (!job || !pageUser) {
			return <div />;
		}
		console.log('u', pageUser);

		return (
			<div className={ s.root }>
				{ actionMode === 'select' &&
					<SelectForm
						username={ username }
						date={ date }
						job={ job }
						location={ location }
						jobLocation={ jobLocation }
						attemptId={ jobLocation.attemptId }
						setActionMode={ setActionMode }
						canEdit={ canEdit }
						postCompleteTask={ postCompleteTask }
					/>
				}
				{ actionMode === 'attempt' &&
					<AttemptForm job={ job } />
				}
				{ actionMode === 'deliver' &&
					<DeliverForm job={ job } />
				}
				{ actionMode === 'serve' &&
					<ServeForm job={ job } />
				}
				{ actionMode === 'badAddress' &&
					<BadAddressForm job={ job } />
				}
			</div>
		);
	}
);

export const components = {
	main: Main,
};

export default Main;
